<template>
  <svg
    class="histogram"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    :viewBox="viewBox"
  >
    <path :d="path" />
  </svg>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  const ASPECT = 8;
  type Point = [number, number];

  interface Props {
    min: number;
    max: number;
    histogram: Record<string, number>;
  }

  const props = defineProps<Props>();

  const height = computed(() => Math.ceil(100 / ASPECT));
  const viewBox = computed(() => `0 -0.5 100 ${height.value + 3}`);

  const line = (pointA: Point, pointB: Point) => {
    const lengthX = pointB[0] - pointA[0];
    const lengthY = pointB[1] - pointA[1];

    return {
      angle: Math.atan2(lengthY, lengthX),
      length: Math.sqrt(lengthX ** 2 + lengthY ** 2),
    };
  };

  const controlPoint = (current: Point, previous?: Point, next?: Point, reverse?: boolean): Point => {
    const p = previous || current;
    const n = next || current;
    const smoothing = 0.2;
    const o = line(p, n);
    const angle = o.angle + (reverse ? Math.PI : 0);
    const length = o.length * smoothing;

    return [current[0] + Math.cos(angle) * length, current[1] + Math.sin(angle) * length];
  };

  const bezierCommand = (arr: Point[], i: number): string => {
    const point = arr[i];
    const [cpsX, cpsY] = controlPoint(arr[i - 1], arr[i - 2], point);
    const [cpeX, cpeY] = controlPoint(point, arr[i - 1], arr[i + 1], true);

    return `C ${cpsX},${cpsY} ${cpeX},${cpeY} ${point[0]},${point[1]}`;
  };

  const adjustedPoints = computed<Point[]>(() => {
    const xs = Object.keys(props.histogram).map(Number);
    const ys = Object.values(props.histogram);
    const maxY = Math.max(...ys);
    const la = Math.log(props.min);
    const lb = Math.log(props.max);
    const width = lb - la;

    const ret = xs.map((x: number, i: number) => {
      const y = ys[i];
      const x1 = i === 0 ? props.min : x;
      const x2 = i === xs.length - 1 ? props.max : xs[i + 1];
      const lx = Math.log(Math.sqrt(x1 * x2));

      return [((lx - la) / width) * 100, (y / maxY) * height.value] as Point;
    });
    ret[0][0] = 0;
    ret[ret.length - 1][0] = 100;
    ret.push([100, 0]);

    return ret as Point[];
  });

  const path = computed(() => {
    const commands = adjustedPoints.value.map(([x, y], i) => {
      if (i === 0) {
        return `M ${x},${y}`;
      }

      return bezierCommand(adjustedPoints.value, i);
    });
    commands.push('L 0,0');

    return commands.join(' ');
  });
</script>

<style scoped lang="scss">
  .histogram {
    --color-fill: color-mix(in srgb, var(--alias-color-background-accent-green) 10%, transparent);
    --color-stroke: color-mix(in srgb, var(--alias-color-border-accent-green) 40%, transparent);
    vertical-align: top;
    width: 100%;
    height: auto;
    fill: var(--color-fill, var(--alias-color-background-accent-green));
    stroke: var(--color-stroke, var(--alias-color-background-accent-green));
    transform: scaleY(-1);
    stroke-width: 0.3px;
  }
</style>
